import { useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import GestureIcon from '@mui/icons-material/Gesture';
import { INCLUSION_OPTIONS } from '@ast-data/shared';
import RepeaterList from '../../components/RepeaterList';
import VocabSuggestField from './VocabSuggestField';
import type { InclusionRow } from './inclusionRows';

// ---------------------------------------------------------------------------
// The Einschlüsse/Beimengungen list editor, shared by the Planum and Profil
// forms — a RepeaterList like the Farben beside it.
// ---------------------------------------------------------------------------

export default function InclusionListEditor({
    rows,
    onChange
}: {
    rows: InclusionRow[];
    onChange: (rows: InclusionRow[]) => void;
}) {
    const intl = useIntl();
    const fromSketch = intl.formatMessage({
        id: 'planum.inclusion.fromSketch'
    });

    return (
        <RepeaterList
            rows={rows}
            onChange={onChange}
            blankRow={{ inclusion: '', percentage: '' }}
            labelId="planum.field.inclusions"
            emptyId="planum.inclusion.empty"
            addId="planum.inclusion.add"
            removeId="planum.inclusion.remove"
            renderRow={(row, update) => (
                <>
                    {/* The sketch's icon on a row the sketch wrote, as on the
                        Schichten (see StratumListEditor). */}
                    {row.sourcePen && (
                        <Tooltip title={fromSketch}>
                            <GestureIcon
                                fontSize="small"
                                color="action"
                                titleAccess={fromSketch}
                                sx={{ alignSelf: 'center', flexShrink: 0 }}
                            />
                        </Tooltip>
                    )}
                    <Box sx={{ flex: 1 }}>
                        <VocabSuggestField
                            value={row.inclusion}
                            onChange={(inclusion) => update({ inclusion })}
                            options={INCLUSION_OPTIONS}
                            labelId="planum.field.inclusion"
                        />
                    </Box>
                    <TextField
                        value={row.percentage}
                        onChange={(event) =>
                            update({ percentage: event.target.value })
                        }
                        label={intl.formatMessage({
                            id: 'planum.field.inclusionPercentage'
                        })}
                        // The Anteil is a percentage — the unit sits in the
                        // field, not in the value.
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        %
                                    </InputAdornment>
                                )
                            }
                        }}
                        sx={{ width: 130, flexShrink: 0 }}
                    />
                </>
            )}
        />
    );
}
